import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";

export default function PieChartAdvanced({
  percentage,
  label,
  count,
  color = "#D4B896",
  delay = 0,
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Calculate the stroke dash array for the percentage
  const circumference = 2 * Math.PI * 20;
  const dashArray = `${(percentage / 100) * circumference} ${circumference}`;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col items-center gap-4"
    >
      <div className="text-[#D4B896] text-lg sm:text-xl font-normal">
        {count}
      </div>
      <div className="relative w-48 h-48 sm:w-56 sm:h-56">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 224 224">
          {/* Background circle */}
          <motion.circle
            cx="112"
            cy="112"
            r="110"
            fill="none"
            stroke="white"
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={isInView ? { pathLength: 1 } : {}}
            transition={{ duration: 0.8, delay: delay + 0.2 }}
          />
          <circle
            cx="112"
            cy="112"
            r="110"
            fill="none"
            stroke="rgba(255, 255, 255, 0.1)"
            strokeWidth="2"
          />

          {/* Percentage arc */}
          <motion.circle
            cx="112"
            cy="112"
            r="110"
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeDasharray={dashArray}
            strokeLinecap="round"
            initial={{ strokeDashoffset: circumference }}
            animate={isInView ? { strokeDashoffset: 0 } : {}}
            transition={{ duration: 1.2, delay: delay + 0.4, ease: "easeOut" }}
          />
        </svg>

        <div className="absolute inset-0 flex flex-col justify-evenly items-center">
          {/* Center text */}
          <div className="flex flex-col items-center justify-center">
            <motion.p
              className="text-white text-center p-2 text-xs sm:text-sm max-w-[120px] leading-tight break-words w-full"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: delay + 0.6 }}
            >
              {label}
            </motion.p>
          </div>

          {/* Percentage display */}
          <motion.div
            className="flex items-center justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: delay + 0.8 }}
          >
            <p className="text-white text-center text-2xl sm:text-3xl font-normal">
              {percentage}%
            </p>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
